import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Moon, Sun, AlertTriangle, Users } from 'lucide-react';
import { calculateNightShiftMetrics, formatMinutes } from '@/utils/timeFormat';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  useCreateScheduleException,
  useCheckShiftLimit,
  useCheckWorkedDay,
} from '@/hooks/schedules/useScheduleExceptions';
import { useCreateShiftReplacement } from '@/hooks/schedules/useShiftReplacement';
import { usePermissions } from '@/hooks/usePermissions';
import type { CreateScheduleExceptionDto, ScheduleType } from '@/types/schedule.types';

// Employee schedule info for replacement filtering
interface EmployeeWithSchedule {
  id: string;
  name: string;
  position?: string | null;
  isWorkingDay: boolean;
  shiftStart?: string | null;
  shiftEnd?: string | null;
}

interface CreateExceptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string | null;
  date: string | null;
  employeeName?: string;
  // Employees with their schedules for the selected date
  employeesWithSchedule?: EmployeeWithSchedule[];
}

const SCHEDULE_TYPES: { value: ScheduleType; label: string }[] = [
  { value: 'working', label: 'Рабочий день' },
  { value: 'day_off', label: 'Выходной' },
  { value: 'holiday', label: 'Праздник' },
  { value: 'reduced', label: 'Сокращённый день' },
];

export const CreateExceptionDialog: React.FC<CreateExceptionDialogProps> = ({
  open,
  onOpenChange,
  employeeId,
  date,
  employeeName,
  employeesWithSchedule = [],
}) => {
  const [isWorkingDay, setIsWorkingDay] = useState(true);
  const [isReplacement, setIsReplacement] = useState(false);
  const [isOvertime, setIsOvertime] = useState(false);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('working');
  const [replacementEmployeeId, setReplacementEmployeeId] = useState<string | null>(null);
  // Открытая смена (аукцион XP) вместо ручного выбора заменяющего
  const [useAuction, setUseAuction] = useState(false);
  const [showAuctionAdvanced, setShowAuctionAdvanced] = useState(false);
  const [baseXp, setBaseXp] = useState('20');
  const [xpStep, setXpStep] = useState('10');
  const [xpCap, setXpCap] = useState('100');
  const [offerTtl, setOfferTtl] = useState('30');
  const [cooldown, setCooldown] = useState('0');

  const [submitError, setSubmitError] = useState<string | null>(null);
  // Предупреждения из ответа бэкенда (исключение создано сверх лимита админом)
  const [submitWarnings, setSubmitWarnings] = useState<string[]>([]);

  const { hasPermission, isAdmin } = usePermissions();
  const canRunAuction = hasPermission('smart_schedule', 'write');
  // Полный администратор может создать исключение сверх лимита смен (с предупреждением)
  const canOverrideShiftLimit = isAdmin();

  const createException = useCreateScheduleException();
  const createAuction = useCreateShiftReplacement();

  // Проверка лимита смен из штатки 1С (по должности получающего смену сотрудника)
  const shiftLimitCheck = useCheckShiftLimit({
    employeeId,
    date,
    isWorkingDay,
    isReplacement,
    replacementEmployeeId,
    enabled:
      open &&
      !useAuction &&
      (isWorkingDay || isReplacement) &&
      (!isReplacement || !!replacementEmployeeId),
  });
  const shiftLimit = shiftLimitCheck.data;
  const shiftLimitExceeded =
    !!shiftLimit?.applicable && !!shiftLimit?.exceeded;
  // Лимит месяца уже превышен ранее, но текущее действие смену не добавляет
  // (день уже рабочий / замена внутри должности) — предупреждаем, не блокируя
  const shiftLimitAlreadyOver =
    !shiftLimitExceeded &&
    !!shiftLimit?.applicable &&
    !!shiftLimit?.hasLimit &&
    shiftLimit.currentShifts > shiftLimit.maxShifts;

  // Защита от затирания отработанного дня: если у сотрудника уже есть отметки
  // за эту дату, исключение-выходной (или замена) бэкенд заблокирует.
  // Полный админ может обойти только явным подтверждением (чекбокс).
  const [confirmWorkedDay, setConfirmWorkedDay] = useState(false);
  const workedDayCheck = useCheckWorkedDay({
    employeeId,
    date,
    enabled: open,
  });
  const workedDay = workedDayCheck.data;
  // Конфликт есть только когда итоговый день — выходной (или это замена)
  const workedDayConflict =
    !!workedDay?.has_worked_shift && (!isWorkingDay || isReplacement);
  const workedDayCheckInLabel = workedDay?.check_in_time
    ? format(new Date(workedDay.check_in_time), 'HH:mm')
    : null;

  const {
    register,
    handleSubmit,
    reset,
    watch,
  } = useForm<{
    shift_start: string;
    shift_end: string;
    reason: string;
    comment: string;
  }>();

  // Watch shift times for real-time calculation
  const shiftStart = watch('shift_start');
  const shiftEnd = watch('shift_end');

  // Calculate night shift metrics in real-time
  const nightMetrics = useMemo(() => {
    if (!isWorkingDay || isReplacement) return null;
    return calculateNightShiftMetrics(shiftStart, shiftEnd);
  }, [shiftStart, shiftEnd, isWorkingDay, isReplacement]);

  // Reset form when dialog opens - use real schedule times if available
  useEffect(() => {
    if (open) {
      // Find current employee's schedule for this date
      const currentEmployee = employeesWithSchedule?.find(
        (emp) => emp.id === employeeId
      );

      // Use real schedule times or fallback to defaults
      const defaultStart = currentEmployee?.shiftStart?.slice(0, 5) || '09:00';
      const defaultEnd = currentEmployee?.shiftEnd?.slice(0, 5) || '18:00';

      reset({
        shift_start: defaultStart,
        shift_end: defaultEnd,
        reason: '',
        comment: '',
      });
      setIsWorkingDay(true);
      setIsReplacement(false);
      // Если базово у сотрудника выходной — рабочее исключение = выход в выходной
      // = сверхурочные по умолчанию. Пользователь может вручную снять.
      setIsOvertime(currentEmployee ? !currentEmployee.isWorkingDay : false);
      setScheduleType('working');
      setReplacementEmployeeId(null);
      setUseAuction(false);
      setShowAuctionAdvanced(false);
      setBaseXp('20');
      setXpStep('10');
      setXpCap('100');
      setOfferTtl('30');
      setCooldown('0');
      setSubmitError(null);
      setSubmitWarnings([]);
      setConfirmWorkedDay(false);
    }
  }, [open, reset, employeeId, employeesWithSchedule]);

  const onSubmit = async (formData: {
    shift_start: string;
    shift_end: string;
    reason: string;
    comment: string;
  }) => {
    if (!employeeId || !date) return;

    // Режим «Открытая смена»: запускаем аукцион XP вместо прямого исключения.
    // Исключение (выходной заменяемому + смена сменщику) создаст acceptOffer,
    // когда кто-то из кандидатов согласится. Локацию бэкенд резолвит сам.
    if (isReplacement && useAuction) {
      try {
        await createAuction.mutateAsync({
          absent_employee_id: employeeId,
          date,
          base_xp: Number(baseXp),
          xp_step: Number(xpStep),
          xp_cap: Number(xpCap),
          offer_ttl_min: Number(offerTtl),
          cooldown_min: Number(cooldown),
        });
        onOpenChange(false);
      } catch (error) {
        console.error('Failed to start shift replacement auction:', error);
      }
      return;
    }

    const dto: CreateScheduleExceptionDto = {
      employee_id: employeeId,
      date,
      is_working_day: isWorkingDay,
      schedule_type: scheduleType,
      reason: formData.reason || undefined,
      comment: formData.comment || 'Исключение создано через админ панель',
      is_replacement: isReplacement,
      replacement_employee_id: isReplacement ? replacementEmployeeId || undefined : undefined,
      is_overtime: isOvertime,
      // Обход защиты отработанного дня — только полный админ и только
      // с явно поставленным чекбоксом подтверждения
      force_worked_day:
        workedDayConflict && canOverrideShiftLimit && confirmWorkedDay
          ? true
          : undefined,
    };

    // Add shift times only for working days
    // Backend expects HH:mm:ss format, but input type="time" gives HH:mm
    if (isWorkingDay && !isReplacement) {
      dto.new_shift_start = formData.shift_start ? `${formData.shift_start}:00` : undefined;
      dto.new_shift_end = formData.shift_end ? `${formData.shift_end}:00` : undefined;
    }

    try {
      setSubmitError(null);
      const created = await createException.mutateAsync(dto);
      // Исключение создано сверх лимита (полный админ): не закрываем диалог
      // молча, а показываем предупреждение из ответа бэкенда
      if (created?.warnings?.length) {
        setSubmitWarnings(created.warnings);
        return;
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create exception:', error);
      const message =
        (error as { message?: string })?.message ||
        'Не удалось создать исключение';
      setSubmitError(message);
    }
  };

  // У текущего сотрудника есть смена в этот день?
  // Замену можно делать только из рабочего дня (некого заменять, если выходной).
  const currentEmployeeIsWorking = useMemo(() => {
    const current = employeesWithSchedule.find((emp) => emp.id === employeeId);
    return current?.isWorkingDay ?? false;
  }, [employeesWithSchedule, employeeId]);

  // Если current не рабочий — сбрасываем тоггл замены
  useEffect(() => {
    if (!currentEmployeeIsWorking && isReplacement) {
      setIsReplacement(false);
      setReplacementEmployeeId(null);
      setUseAuction(false);
    }
  }, [currentEmployeeIsWorking, isReplacement]);

  // Выключили замену — гасим режим аукциона
  useEffect(() => {
    if (!isReplacement && useAuction) {
      setUseAuction(false);
    }
  }, [isReplacement, useAuction]);

  // Доступные заменяющие:
  // 1. Не сам заменяемый
  // 2. У кого ВЫХОДНОЙ в этот день (они свободны и могут выйти на смену)
  const availableReplacementEmployees = useMemo(() => {
    return employeesWithSchedule.filter(
      (emp) => emp.id !== employeeId && !emp.isWorkingDay
    );
  }, [employeesWithSchedule, employeeId]);

  const formattedDate = date
    ? format(new Date(date), 'd MMMM yyyy (EEEE)', { locale: ru })
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Создать исключение в графике</DialogTitle>
          <DialogDescription>
            {employeeName && (
              <span className="font-medium">{employeeName}</span>
            )}
            {date && (
              <span className="block mt-1 text-sm">{formattedDate}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Schedule Type */}
          <div className="space-y-2">
            <Label>Тип дня</Label>
            <Select
              value={scheduleType}
              onValueChange={(value: ScheduleType) => {
                setScheduleType(value);
                // Auto-set is_working_day based on type
                setIsWorkingDay(value === 'working' || value === 'reduced');
              }}
              disabled={isReplacement}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCHEDULE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Is Working Day Toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="is-working">Рабочий день</Label>
            <Switch
              id="is-working"
              checked={isWorkingDay}
              onCheckedChange={setIsWorkingDay}
              disabled={isReplacement}
            />
          </div>

          {/* Replacement Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="is-replacement">Замена сотрудника</Label>
              <p className="text-xs text-muted-foreground mt-1">
                {currentEmployeeIsWorking
                  ? 'Сотрудник получит выходной, его смена перейдёт заменяющему'
                  : 'У сотрудника в этот день нет смены — замена недоступна'}
              </p>
            </div>
            <Switch
              id="is-replacement"
              checked={isReplacement}
              disabled={!currentEmployeeIsWorking}
              onCheckedChange={(checked) => {
                setIsReplacement(checked);
                if (checked) {
                  // Employee being replaced gets day off
                  setIsWorkingDay(false);
                  setScheduleType('day_off');
                  // Смена сменщика по умолчанию сверхурочная
                  setIsOvertime(true);
                }
              }}
            />
          </div>

          {/* Overtime Toggle — в режиме аукциона неприменимо (исключение создаётся позже) */}
          {!(isReplacement && useAuction) && (
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="is-overtime">Сверхурочные</Label>
              {isReplacement ? (
                <p className="text-xs text-muted-foreground mt-1">
                  Применяется к смене заменяющего сотрудника
                </p>
              ) : (
                !currentEmployeeIsWorking &&
                isWorkingDay &&
                isOvertime && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Включено автоматически: выход в выходной день
                  </p>
                )
              )}
            </div>
            <Switch
              id="is-overtime"
              checked={isOvertime}
              onCheckedChange={setIsOvertime}
            />
          </div>
          )}

          {/* Выбор способа замены: вручную или открытая смена (аукцион) */}
          {isReplacement && canRunAuction && (
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="use-auction">Открытая смена (аукцион)</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {useAuction
                    ? 'Система обзвонит свободных сотрудников той же должности, начиная с того, кто дольше отдыхал, повышая XP до согласия. Первое предложение уйдёт сразу.'
                    : 'Не назначать конкретного — запустить заявку на открытую смену.'}
                </p>
              </div>
              <Switch
                id="use-auction"
                checked={useAuction}
                onCheckedChange={setUseAuction}
              />
            </div>
          )}

          {/* Параметры бонусов аукциона */}
          {isReplacement && useAuction && (
            <div className="space-y-2">
              <button
                type="button"
                className="text-sm text-muted-foreground underline"
                onClick={() => setShowAuctionAdvanced((v) => !v)}
              >
                {showAuctionAdvanced ? 'Скрыть' : 'Доп. настройки бонусов'}
              </button>
              {showAuctionAdvanced && (
                <div className="grid grid-cols-2 gap-3 rounded-md border p-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Старт XP</Label>
                    <Input type="number" min={0} value={baseXp} onChange={(e) => setBaseXp(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Шаг XP</Label>
                    <Input type="number" min={0} value={xpStep} onChange={(e) => setXpStep(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Потолок XP</Label>
                    <Input type="number" min={0} value={xpCap} onChange={(e) => setXpCap(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ответ, мин</Label>
                    <Input type="number" min={1} value={offerTtl} onChange={(e) => setOfferTtl(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Кулдаун, мин</Label>
                    <Input type="number" min={0} value={cooldown} onChange={(e) => setCooldown(e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Replacement Employee Selector — только в ручном режиме */}
          {isReplacement && !useAuction && (
            <div className="space-y-2">
              <Label>Заменяющий сотрудник</Label>
              <p className="text-xs text-muted-foreground">
                Показаны только сотрудники с выходным днём на эту дату — они свободны и могут выйти на смену
              </p>
              {availableReplacementEmployees.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  Нет свободных сотрудников на эту дату — все либо работают, либо отсутствуют
                </div>
              ) : (
                <Select
                  value={replacementEmployeeId || ''}
                  onValueChange={(value) => setReplacementEmployeeId(value || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите сотрудника" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableReplacementEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        <div className="flex items-center gap-2">
                          <span>{emp.name}</span>
                          {emp.position && (
                            <span className="text-muted-foreground">
                              ({emp.position})
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            Выходной
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Shift Times (only for working days and not replacement) */}
          {isWorkingDay && !isReplacement && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shift_start">Начало смены</Label>
                  <Input
                    id="shift_start"
                    type="time"
                    {...register('shift_start')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shift_end">Конец смены</Label>
                  <Input
                    id="shift_end"
                    type="time"
                    {...register('shift_end')}
                  />
                </div>
              </div>

              {/* Night shift info - real-time calculation */}
              {nightMetrics && nightMetrics.totalMinutes > 0 && (
                <div className="flex flex-wrap items-center gap-3 text-sm bg-muted/50 p-3 rounded-md border">
                  {nightMetrics.crossesMidnight && (
                    <span className="flex items-center gap-1.5 text-indigo-600 font-medium">
                      <Moon className="h-4 w-4" />
                      Ночная смена
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Sun className="h-4 w-4 text-amber-500" />
                    Дневных: {formatMinutes(nightMetrics.dayMinutes)}
                  </span>
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Moon className="h-4 w-4 text-indigo-400" />
                    Ночных: {formatMinutes(nightMetrics.nightMinutes)}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Лимит смен из штатки 1С */}
          {!useAuction && shiftLimit?.applicable && shiftLimit.hasLimit && (
            <div
              className={`flex items-start gap-2 text-sm p-3 rounded-md border ${
                shiftLimitExceeded
                  ? canOverrideShiftLimit
                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                    : 'bg-red-50 border-red-300 text-red-900'
                  : shiftLimitAlreadyOver
                    ? 'bg-amber-50 border-amber-300 text-amber-900'
                    : 'bg-muted/50'
              }`}
            >
              {shiftLimitExceeded || shiftLimitAlreadyOver ? (
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              ) : (
                <Users className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
              )}
              <div>
                <p>
                  Смены по должности{' '}
                  <span className="font-medium">
                    {shiftLimit.position?.name}
                  </span>
                  {shiftLimit.department?.name && (
                    <> ({shiftLimit.department.name})</>
                  )}
                  : занято {shiftLimit.currentShifts} из {shiftLimit.maxShifts}{' '}
                  в этом месяце
                </p>
                {shiftLimitExceeded && (
                  <p className="mt-1 font-medium">
                    {canOverrideShiftLimit
                      ? `Перелимит: после сохранения будет ${shiftLimit.projectedShifts} из ${shiftLimit.maxShifts} смен. Вы администратор — исключение будет создано сверх лимита штатного расписания.`
                      : `Перелимит: сохранение добавит ${shiftLimit.projectedShifts}-ю смену при лимите ${shiftLimit.maxShifts}. Создание исключения недоступно.`}
                  </p>
                )}
                {shiftLimitAlreadyOver && (
                  <p className="mt-1 font-medium">
                    Лимит месяца уже превышен. Это действие не добавляет новую
                    смену (день уже рабочий или замена в рамках должности),
                    поэтому сохранение не блокируется.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Защита от затирания отработанного дня */}
          {workedDayConflict && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-md border bg-red-50 border-red-300 text-red-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="space-y-2">
                <p className="font-medium">
                  Сотрудник уже отработал этот день
                  {workedDayCheckInLabel && <> — отметка прихода в {workedDayCheckInLabel}</>}
                </p>
                {useAuction ? (
                  <p>
                    Открытая смена на отработанный день недоступна: заменяемый
                    уже вышел на смену.
                  </p>
                ) : canOverrideShiftLimit ? (
                  <>
                    <p>
                      Выходной или замена сотрёт фактическую смену из графика.
                      Отметки прихода сохранятся, но день будет показан как
                      выходной.
                    </p>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox
                        checked={confirmWorkedDay}
                        onCheckedChange={(checked) =>
                          setConfirmWorkedDay(checked === true)
                        }
                        className="mt-0.5"
                      />
                      <span>
                        Подтверждаю: проставить выходной несмотря на отметки
                        (например, отметка ошибочна)
                      </span>
                    </label>
                  </>
                ) : (
                  <p>
                    Проставить выходной или замену на отработанный день нельзя —
                    фактическая смена перестанет отображаться в графике.
                    Обратитесь к администратору, если отметка ошибочна.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Ошибка сохранения */}
          {submitError && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-md border bg-red-50 border-red-300 text-red-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>{submitError}</p>
            </div>
          )}

          {/* Исключение создано сверх лимита (админ) */}
          {submitWarnings.length > 0 && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-md border bg-amber-50 border-amber-300 text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Исключение создано с предупреждением:</p>
                {submitWarnings.map((w, i) => (
                  <p key={i} className="mt-1">{w}</p>
                ))}
              </div>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Причина</Label>
            <Input
              id="reason"
              placeholder="Например: Больничный лист, Отпуск по заявлению"
              {...register('reason')}
            />
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment">Комментарий</Label>
            <Textarea
              id="comment"
              placeholder="Дополнительная информация..."
              rows={2}
              {...register('comment')}
            />
          </div>

          <DialogFooter>
            {submitWarnings.length > 0 ? (
              <Button type="button" onClick={() => onOpenChange(false)}>
                Понятно
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createException.isPending ||
                    createAuction.isPending ||
                    (isReplacement && !useAuction && !replacementEmployeeId) ||
                    (!useAuction && shiftLimitExceeded && !canOverrideShiftLimit) ||
                    (workedDayConflict &&
                      (useAuction ||
                        !canOverrideShiftLimit ||
                        !confirmWorkedDay))
                  }
                >
                  {isReplacement && useAuction
                    ? createAuction.isPending
                      ? 'Запуск...'
                      : 'Запустить аукцион'
                    : createException.isPending
                      ? 'Создание...'
                      : 'Создать'}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateExceptionDialog;
